import { generate } from '../generate'
import { baseSchema, Update } from '../base'
import * as z from 'zod'
import {
  prettifyBlockType,
  blockTypesRecord,
  BlockTypes,
} from './block-types-record'
import { listViewSchema } from './list'
import { branchLineageSchema } from '../branch'
import {
  workflowTriggerSchema,
  workflowActionSchema,
  workflowPayloadSchema,
} from './workflow'

/* -------------------------------------------------------------------------------------------------
 * Reusable ZOD schemas
 * -----------------------------------------------------------------------------------------------*/

// array of sets sourceBlockIds
const variantSetsConstraintSchema = z.array(z.string()).nullish()
export type VariantSetsConstraintSchema = z.infer<
  typeof variantSetsConstraintSchema
>

export const baseConstraintsSchema = z.object({
  isRequired: z.boolean(),
  acceptedVariantSets: variantSetsConstraintSchema,
})
export type BaseConstraintsSchema = z.infer<typeof baseConstraintsSchema>

export const searchConfigSchema = z.object({ index: z.boolean() }).optional()
export type SearchConfigSchema = z.infer<typeof searchConfigSchema>

export const blockVariantsSchemaGen = <T extends { value: z.ZodType }>(
  value: T
) => {
  return z.record(z.string(), z.object(value)).nullish()
}

export const componentDataSchema = z
  .object({
    title: z.string().nullable(),
    description: z.string().nullable(),
    icon: z.string().nullable(),
  })
  .nullable()

export type ComponentDataSchema = z.infer<typeof componentDataSchema>

/* -------------------------------------------------------------------------------------------------
 * TipTap Block Value
 * -----------------------------------------------------------------------------------------------*/

// not a field, but rather a value that could be used by various fields:
export const tipTapBlockValueSchema = z.object({
  state: z.string(),
  // This date will help active clients know if they should discard their changes or not, after a revert
  lastRevertedAt: z.string().nullish(),
})

export type TipTapBlockValue = z.infer<typeof tipTapBlockValueSchema>

/* -------------------------------------------------------------------------------------------------
 * Block Types
 * -----------------------------------------------------------------------------------------------*/

export const rootBlockSchema = z.object({
  type: z.literal(blockTypesRecord.root),
  typeOptions: z.object({}),
  value: z.object({}),
  constraints: z.object({}),
  searchConfig: searchConfigSchema,
  branchId: z.string(),
  branchLineage: z.lazy(() => branchLineageSchema).optional(),
  lastRevertedAt: z.string().nullish(),
  isInlineSuggestion: z.literal(true).optional(),
})

/* "Layout" Blocks
 * -----------------------------------------------------------------------------------------------*/

export const commentBlockSchema = z.object({
  type: z.literal(blockTypesRecord.comment),
  typeOptions: z.object({}),
  value: tipTapBlockValueSchema,
  constraints: z.object({}),
  searchConfig: searchConfigSchema,
})

export const documentBlockSchema = z.object({
  type: z.literal(blockTypesRecord.document),
  typeOptions: z.object({}),
  value: z.object({}),
  constraints: z.object({
    acceptedVariantSets: variantSetsConstraintSchema,
  }),
  previewUrl: z.string().nullish(),
  searchConfig: searchConfigSchema,
})

export const componentTypeOptionsSchema = z.object({
  displayTitle: z.string(),
  displayDescription: z.string(),
  displayIcon: z.string(),
  displayImage: z
    .object({
      url: z.string(),
      mimeType: z.string(),
      width: z.number(),
      height: z.number(),
    })
    .nullish(),
})
export type ComponentTypeOptions = z.infer<typeof componentTypeOptionsSchema>

// renders like a document, but its similar to a figma component
export const componentBlockSchema = z.object({
  type: z.literal(blockTypesRecord.component),
  typeOptions: componentTypeOptionsSchema,
  value: z.object({}),
  constraints: z.object({
    acceptedVariantSets: variantSetsConstraintSchema,
  }),
  previewUrl: z.string().nullish(),
  searchConfig: searchConfigSchema.and(
    z
      .object({
        generateEmbeddings: z
          .object({
            enabled: z.boolean(),
            systemPrompt: z.string().nullish(),
          })
          .nullish(),
      })
      .nullish()
  ),
})

export const instanceTypeOptionsSchema = z.object({
  targetBlockId: z.string(),
})
export type InstanceTypeOptions = z.infer<typeof instanceTypeOptionsSchema>

export const instanceBlockSchema = z.object({
  type: z.literal(blockTypesRecord.instance),
  typeOptions: instanceTypeOptionsSchema,
  value: z.object({}),
  variantOverrides: blockVariantsSchemaGen({
    value: z.never().optional(),
    title: z.string(),
    slug: z.string().nullish(),
  }),
  constraints: z.object({}),
  previewUrl: z.string().nullish(),
  searchConfig: searchConfigSchema,
})

const listTypeOptionsSchema = instanceTypeOptionsSchema.pick({
  targetBlockId: true,
})
export type ListTypeOptions = z.infer<typeof listTypeOptionsSchema>

export * from './list'

export const listBlockSchema = z.object({
  type: z.literal(blockTypesRecord.list),
  typeOptions: listTypeOptionsSchema,
  value: z.object({
    views: z.array(listViewSchema).optional(),
  }),
  constraints: baseConstraintsSchema.pick({ isRequired: true }).and(
    z.object({
      minRows: z.number().nullable(),
      maxRows: z.number().nullable(),
    })
  ),
  previewUrl: z.string().nullish(),
  searchConfig: searchConfigSchema,
})

/* "Primitive" Blocks
 * -----------------------------------------------------------------------------------------------*/

export const textBlockSchema = z.object({
  type: z.literal(blockTypesRecord.text),
  typeOptions: z.object({}),
  value: z.string().nullable(),
  variantOverrides: blockVariantsSchemaGen({ value: z.string().nullable() }),
  constraints: baseConstraintsSchema.and(
    z.object({
      minLength: z.number().nullable(),
      maxLength: z.number().nullable(),
      regexPattern: z.string().nullable(),
    })
  ),
  searchConfig: searchConfigSchema,
})

export const selectBlockSchema = z.object({
  type: z.literal(blockTypesRecord.select),
  typeOptions: z.object({}),
  value: z.array(z.string()),
  variantOverrides: blockVariantsSchemaGen({ value: z.array(z.string()) }),
  constraints: baseConstraintsSchema.and(
    z.object({
      acceptedValues: z.array(z.string()),
      multiple: z.boolean(),
      max: z.number(),
    })
  ),
  searchConfig: searchConfigSchema,
})

export const referenceConstraintsSchema = baseConstraintsSchema.and(
  z.object({
    acceptedTypes: z.array(z.string()).nullable(),
    multiple: z.boolean(),
    startCollapsed: z.boolean().nullable(),
    disableLinkExisting: z.boolean().nullable(),
    disableCreateNew: z.boolean().nullable(),
    min: z.number().nullish(),
    max: z.number().nullish(),
  })
)

export type ReferenceConstraints = z.infer<typeof referenceConstraintsSchema>

export const referenceBlockSchema = z.object({
  type: z.literal(blockTypesRecord.reference),
  typeOptions: z.object({}),
  value: z.object({
    targetBlockIds: z.array(z.string()),
  }),
  variantOverrides: blockVariantsSchemaGen({
    value: z.object({ targetBlockIds: z.array(z.string()) }),
  }),
  constraints: referenceConstraintsSchema,
  searchConfig: searchConfigSchema,
})

export const colorBlockSchema = z.object({
  type: z.literal(blockTypesRecord.color),
  typeOptions: z.object({}),
  value: z.string(),
  variantOverrides: blockVariantsSchemaGen({ value: z.string() }),
  constraints: baseConstraintsSchema,
  searchConfig: searchConfigSchema,
})

export const numberBlockSchema = z.object({
  type: z.literal(blockTypesRecord.number),
  typeOptions: z.object({}),
  value: z.number().nullable(),
  variantOverrides: blockVariantsSchemaGen({ value: z.number().nullable() }),
  constraints: baseConstraintsSchema.and(
    z.object({
      minValue: z.number().nullable(),
      maxValue: z.number().nullable(),
    })
  ),
  searchConfig: searchConfigSchema,
})

export const richTextLayouts = z.enum(['single-line', 'multi-line'])
export type RichTextLayouts = z.infer<typeof richTextLayouts>

const richTextOverridableValueSchema = tipTapBlockValueSchema.extend({
  /**
   * On commit transformations (like to html, markdown, etc) to make the api faster.
   */
  transformations: z
    .object({
      version: z.number(),
      html: z.string().optional(),
      markdown: z.string().optional(),
      plain: z.string().optional(),
      htmlToc: z.string().optional(),
      htmlTocSlugs: z.string().optional(),
      jsonToc: z.any().optional(),
    })
    .nullish(),
})

export const richTextBlockSchema = z.object({
  type: z.literal(blockTypesRecord.richText),
  typeOptions: z.object({}),
  value: richTextOverridableValueSchema,
  variantOverrides: blockVariantsSchemaGen({
    value: richTextOverridableValueSchema.omit({ lastRevertedAt: true }),
    hash: z.string(),
    overrideLivingChildren: z.string().array().nullish(),
  }),
  constraints: baseConstraintsSchema.and(
    z.object({
      forbiddenNodes: z.array(z.string()).or(z.literal('all')).nullish(),
      allowedComponents: z.array(z.string()).nullable(),
      allowedInternalLinks: z.array(z.string()).nullish(),
      layout: richTextLayouts.optional(),
    })
  ),
  searchConfig: searchConfigSchema,
})

/**
 * For each media type:
 * - true: accept all valid types (and all {type}/* MIME types)
 * - false: accept no types (same for [])
 * - string[]: accept only the given types
 */
const fileBlockConstraintsSchema = baseConstraintsSchema.and(
  z.object({
    allowedMimeTypes: z
      .object({
        image: z.array(z.string()).or(z.boolean()).nullish(),
        video: z.array(z.string()).or(z.boolean()).nullish(),
        audio: z.array(z.string()).or(z.boolean()).nullish(),
        file: z.array(z.string()).or(z.boolean()).nullish(),
      })
      .nullable(),
    maxSize: z.string().nullable(),
  })
)

export const mediaBlockValueTypeSchema = z.enum([
  'image',
  'video',
  'audio',
  'file',
])
export type MediaBlockValueType = z.infer<typeof mediaBlockValueTypeSchema>

export const imageValueSchema = z.object({
  type: z.literal('image'),
  status: z.literal('uploaded'),
  fileName: z.string(),
  url: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  width: z.number(),
  height: z.number(),
  lastModified: z.number(),
  altText: z.string().nullish(),
  cloudflareImage: z
    .discriminatedUnion('uploadState', [
      z.object({
        uploadState: z.literal('will-not-upload'),
        url: z.null(),
        reason: z.string(),
      }),
      z.object({
        uploadState: z.literal('uploaded'),
        url: z.string(),
        reason: z.null(),
      }),
    ])
    .nullish(),
})

export const videoValueSchema = z.object({
  type: z.literal('video'),
  status: z.literal('uploaded'),
  fileName: z.string(),
  url: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  width: z.number(),
  height: z.number(),
  lastModified: z.number(),
  duration: z.number().nullish(),
})

export const audioValueSchema = z.object({
  type: z.literal('audio'),
  status: z.literal('uploaded'),
  fileName: z.string(),
  url: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  duration: z.number().nullish(),
  lastModified: z.number(),
})

export const fileValueSchema = z.object({
  type: z.literal('file'),
  status: z.literal('uploaded'),
  fileName: z.string(),
  url: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  lastModified: z.number(),
})

const mediaValueSchema = z
  .discriminatedUnion('type', [
    imageValueSchema,
    videoValueSchema,
    audioValueSchema,
    fileValueSchema,
  ])
  .or(
    z.object({
      status: z.literal('idle'),
    })
  )
  .or(
    z.object({
      status: z.literal('uploading'),
    })
  )

export const mediaBlockSchema = z.object({
  type: z.literal(blockTypesRecord.media),
  typeOptions: z.object({}),
  value: mediaValueSchema,
  variantOverrides: blockVariantsSchemaGen({ value: mediaValueSchema }),
  constraints: fileBlockConstraintsSchema,
  searchConfig: searchConfigSchema,
})

export const booleanBlockSchema = z.object({
  type: z.literal(blockTypesRecord.boolean),
  typeOptions: z.object({}),
  value: z.boolean(),
  variantOverrides: blockVariantsSchemaGen({ value: z.boolean() }),
  constraints: z.object({
    acceptedVariantSets: variantSetsConstraintSchema,
  }),
  searchConfig: searchConfigSchema,
})

export const dateBlockSchema = z.object({
  type: z.literal(blockTypesRecord.date),
  typeOptions: z.object({
    useTime: z.boolean(),
  }),
  value: z.string().nullable(),
  variantOverrides: blockVariantsSchemaGen({ value: z.string().nullable() }),
  constraints: baseConstraintsSchema,
  searchConfig: searchConfigSchema,
})

export const codeSnippetBlockSchema = z.object({
  type: z.literal(blockTypesRecord.codeSnippet),
  typeOptions: z.object({}),
  value: z.object({
    code: z.string(),
    language: z.string(),
  }),
  variantOverrides: blockVariantsSchemaGen({
    value: z.object({ code: z.string(), language: z.string() }),
  }),
  constraints: baseConstraintsSchema.and(
    z.object({ allowedLanguages: z.array(z.string()).nullable() })
  ),
  searchConfig: searchConfigSchema,
})

const ogImageBaseValueSchema = z.object({
  positionX: z.number(),
  positionY: z.number(),
  positionZ: z.number(), // layer order - 0 is the bottom
  width: z.number(),
  height: z.number(),
  rotation: z.number(),
  opacity: z.number(),
})

const layersSchema = z.object({
  layersData: z.array(
    ogImageBaseValueSchema.and(
      z.discriminatedUnion('type', [
        z.object({
          type: z.literal('text'),
          textValue: z.string(), // can include variables
          fontFamily: z.string(),
          fontWeight: z.number(),
          fontUrl: z.string(),
          size: z.number(),
          lineHeight: z.number(),
          alignHorizontal: z.enum(['left', 'center', 'right', 'justify']),
          alignVertical: z.enum(['top', 'middle', 'bottom']),
          color: z.string(),
          lineClamp: z.number(),
        }),
        z.object({
          type: z.literal('image'),
          cornerRadius: z.number(),
          objectFit: z.enum(['cover', 'contain', 'fill']),
          imageValue: z.discriminatedUnion('type', [
            z.object({
              type: z.literal('static'),
              value: imageValueSchema.omit({ type: true }).or(
                z.object({
                  status: z.enum(['uploading', 'idle']),
                })
              ),
            }),
            z.object({
              type: z.literal('dynamic'),
              value: z.string(), // template language variable (e.g.: {block.avatar.url})
            }),
          ]),
        }),
        z.object({
          type: z.literal('shape'),
          cornerRadius: z.number(),
          color: z.string(),
        }),
      ])
    )
  ),
  layersDataVersion: z.number(), // enables us to update the layersData format in the future without breaking existing templates
  showTemplates: z.boolean(),
})

export const ogImageBlockSchema = z.object({
  type: z.literal(blockTypesRecord.ogImage),
  typeOptions: z.object({}),
  value: layersSchema.partial(),
  variantOverrides: blockVariantsSchemaGen({
    value: layersSchema.partial(),
  }),
  constraints: layersSchema.and(
    z.object({ acceptedVariantSets: variantSetsConstraintSchema })
  ),
  searchConfig: searchConfigSchema,
})

export * from './icon'

export const iconBlockSchema = z.object({
  type: z.literal(blockTypesRecord.icon),
  typeOptions: z.object({}),
  value: z.string().nullable(),
  variantOverrides: blockVariantsSchemaGen({ value: z.string().nullable() }),
  constraints: baseConstraintsSchema.and(
    z.object({ allowedLibraries: z.record(z.string(), z.boolean()).nullish() })
  ),
  searchConfig: searchConfigSchema,
})

const variantSchema = z.object({
  id: z.string(),
  label: z.string(),
  apiName: z.string(),
  isDefault: z.boolean(),
  color: z.string(),
  hidden: z.boolean(),
})

export type Variant = z.infer<typeof variantSchema>
export const variantSetBlockSchema = z.object({
  type: z.literal(blockTypesRecord.variantSet),
  typeOptions: z.object({}),
  value: z.object({ variants: z.array(variantSchema) }),
  constraints: z.object({}),
  searchConfig: searchConfigSchema,
})

const inputSchemaBase = z.object({
  name: z.string(),
  label: z.string().optional(),
  required: z.boolean(),
  placeholder: z.string().nullish(),
  defaultValue: z.string().nullish(),
  helpText: z.string().nullish(),
  order: z.number().default(1),
})

const inputUnionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.enum([
      'text',
      'textarea',
      'number',
      'date',
      'datetime',
      'email',
      'checkbox',
      'hidden',
    ]),
  }),
  z.object({
    type: z.enum(['select', 'radio']),
    options: z.array(z.string()).default(['']),
    multiple: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('file'),
  }),
])

const inputForApiSchema = inputSchemaBase
  .omit({ order: true, label: true })
  .and(z.object({ id: z.string(), label: z.string() }))
  .and(inputUnionSchema)

const inputSchema = inputSchemaBase.and(inputUnionSchema)

export {
  inputForApiSchema as eventFieldForApiSchema,
  inputSchema as eventFieldSchema,
}

export type EventField = z.infer<typeof inputSchema>

export const eventBlockSchema = z.object({
  type: z.literal(blockTypesRecord.event),
  typeOptions: z.object({
    view: z.enum(['chart', 'table']),
    systemColumnsOrder: z.record(z.string(), z.number()).nullish(),
  }),
  constraints: z.object({
    schema: z.record(z.string(), inputSchema.nullable()),
  }),
  value: z.object({
    schema: z.record(z.string(), inputSchema.nullable()).nullish(),
  }),
  searchConfig: searchConfigSchema,
})

export const workflowBlockSchema = z.object({
  type: z.literal(blockTypesRecord.workflow),
  typeOptions: z.object({}),
  constraints: z.object({}),
  value: z.object({
    trigger: workflowTriggerSchema,
    actions: z.record(workflowActionSchema),
    activeRuns: z.array(
      z.object({
        id: z.string(),
        action: workflowActionSchema,
        trigger: workflowPayloadSchema,
        status: z.enum(['pending', 'running']),
        branchId: z.string(),
      })
    ),
  }),
  searchConfig: searchConfigSchema,
})

/* Special Blocks: These blocks can't be rendered in the tree
 * -----------------------------------------------------------------------------------------------*/

export const agentBlockSchema = z.object({
  type: z.literal(blockTypesRecord.agent),
  typeOptions: z.object({
    model: z.string(),
    avatar: z.string().nullable(),
    accent: z.string().nullish(),
    grayscale: z.string().nullish(),
  }),
  constraints: z.object({
    // content-level stuff
    access: z.object({}),
    edit: z.enum(['disabled', 'enabled']),
    searchTheWeb: z.enum(['disabled', 'enabled']),
    // admin-level stuff
    commit: z.enum(['disabled', 'enabled']),
    getUserInfo: z.enum(['disabled', 'enabled']),
    manageBranches: z.enum(['disabled', 'enabled']),
    // custom
    customTools: z.object({}),
  }),
  value: tipTapBlockValueSchema, // system prompt
  searchConfig: searchConfigSchema,
})

/* -------------------------------------------------------------------------------------------------
 * Block (uniting all block types)
 * -----------------------------------------------------------------------------------------------*/

const block = z.discriminatedUnion('type', [
  rootBlockSchema,
  documentBlockSchema,
  textBlockSchema,
  numberBlockSchema,
  selectBlockSchema,
  commentBlockSchema,
  richTextBlockSchema,
  mediaBlockSchema,
  booleanBlockSchema,
  dateBlockSchema,
  listBlockSchema,
  codeSnippetBlockSchema,
  ogImageBlockSchema,
  instanceBlockSchema,
  componentBlockSchema,
  referenceBlockSchema,
  colorBlockSchema,
  iconBlockSchema,
  variantSetBlockSchema,
  eventBlockSchema,
  workflowBlockSchema,
  agentBlockSchema,
])

const blockBaseSchema = baseSchema.extend({
  title: z.string(),
  description: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  isCommitted: z.boolean(), // means it's immutable.
  hidden: z.boolean(),
  sourceBlockId: z.string().nullish(),
  apiName: z.string().nullish(),
  slug: z.string().nullish(),
  instanceChildTarget: z.string().nullish(),
  playgroundId: z.string().optional(),
})

export const blockSchema = block.and(blockBaseSchema)

export const blockType = z.union([
  z.literal(blockTypesRecord.root),
  z.literal(blockTypesRecord.document),
  z.literal(blockTypesRecord.number),
  z.literal(blockTypesRecord.text),
  z.literal(blockTypesRecord.select),
  z.literal(blockTypesRecord.comment),
  z.literal(blockTypesRecord.richText),
  z.literal(blockTypesRecord.boolean),
  z.literal(blockTypesRecord.date),
  z.literal(blockTypesRecord.media),
  z.literal(blockTypesRecord.list),
  z.literal(blockTypesRecord.codeSnippet),
  z.literal(blockTypesRecord.ogImage),
  z.literal(blockTypesRecord.instance),
  z.literal(blockTypesRecord.component),
  z.literal(blockTypesRecord.reference),
  z.literal(blockTypesRecord.color),
  z.literal(blockTypesRecord.icon),
  z.literal(blockTypesRecord.variantSet),
  z.literal(blockTypesRecord.event),
  z.literal(blockTypesRecord.workflow),
  z.literal(blockTypesRecord.agent),
])

export const blockBaseOpsInternal = generate('block', blockSchema.parse)

/* -------------------------------------------------------------------------------------------------
 * TS Types
 * -----------------------------------------------------------------------------------------------*/

export type BlockBase = z.infer<typeof blockBaseSchema>
export type Block = z.infer<typeof blockSchema>
export type BlockUpdate = Update<Block>
export type BlockType = Block['type']

export type RootBlock = BlockBase & z.infer<typeof rootBlockSchema>
export type DocumentBlock = BlockBase & z.infer<typeof documentBlockSchema>
export type TextBlock = BlockBase & z.infer<typeof textBlockSchema>
export type NumberBlock = BlockBase & z.infer<typeof numberBlockSchema>
export type CommentBlock = BlockBase & z.infer<typeof commentBlockSchema>
export type RichTextBlock = BlockBase & z.infer<typeof richTextBlockSchema>
export type MediaBlock = BlockBase & z.infer<typeof mediaBlockSchema>
export type BooleanBlock = BlockBase & z.infer<typeof booleanBlockSchema>
export type DateBlock = BlockBase & z.infer<typeof dateBlockSchema>
export type ListBlock = BlockBase & z.infer<typeof listBlockSchema>
export type CodeSnippetBlock = BlockBase &
  z.infer<typeof codeSnippetBlockSchema>
export type OgImageBlock = BlockBase & z.infer<typeof ogImageBlockSchema>
export type InstanceBlock = BlockBase & z.infer<typeof instanceBlockSchema>
export type ComponentBlock = BlockBase & z.infer<typeof componentBlockSchema>
export type ReferenceBlock = BlockBase & z.infer<typeof referenceBlockSchema>
export type SelectBlock = BlockBase & z.infer<typeof selectBlockSchema>
export type ColorBlock = BlockBase & z.infer<typeof colorBlockSchema>
export type IconBlock = BlockBase & z.infer<typeof iconBlockSchema>
export type VariantSetBlock = BlockBase & z.infer<typeof variantSetBlockSchema>
export type EventBlock = BlockBase & z.infer<typeof eventBlockSchema>
export type WorkflowBlock = BlockBase & z.infer<typeof workflowBlockSchema>
export type AgentBlock = BlockBase & z.infer<typeof agentBlockSchema>

export type SpecificBlockData =
  | Omit<RootBlock, keyof BlockBase>
  | Omit<DocumentBlock, keyof BlockBase>
  | Omit<TextBlock, keyof BlockBase>
  | Omit<NumberBlock, keyof BlockBase>
  | Omit<CommentBlock, keyof BlockBase>
  | Omit<RichTextBlock, keyof BlockBase>
  | Omit<MediaBlock, keyof BlockBase>
  | Omit<BooleanBlock, keyof BlockBase>
  | Omit<DateBlock, keyof BlockBase>
  | Omit<ListBlock, keyof BlockBase>
  | Omit<CodeSnippetBlock, keyof BlockBase>
  | Omit<OgImageBlock, keyof BlockBase>
  | Omit<InstanceBlock, keyof BlockBase>
  | Omit<ComponentBlock, keyof BlockBase>
  | Omit<ReferenceBlock, keyof BlockBase>
  | Omit<SelectBlock, keyof BlockBase>
  | Omit<ColorBlock, keyof BlockBase>
  | Omit<IconBlock, keyof BlockBase>
  | Omit<VariantSetBlock, keyof BlockBase>
  | Omit<EventBlock, keyof BlockBase>
  | Omit<WorkflowBlock, keyof BlockBase>
  | Omit<AgentBlock, keyof BlockBase>

export type BlockByType<Type extends BlockType> = Extract<Block, { type: Type }>

export { prettifyBlockType, blockTypesRecord, type BlockTypes }

/* -------------------------------------------------------------------------------------------------
 * Change Block
 * ---------------------------------------------------------------------------------------------- */

const standardBlockChange = z.object({
  status: z.enum(['modified', 'unmodified', 'reordered', 'deleted', 'created']),
  previous: blockSchema.nullable(),
  current: blockSchema.nullable(),
  conflicting: blockSchema.nullable(),
})

const conflictType = z.enum([
  'deleted-in-source',
  'deleted-in-head',
  'conflict',
  'reorder-in-both',
])

const conflictBlockChange = z.object({
  status: z.literal('conflict'),
  previous: blockSchema,
  current: blockSchema,
  conflicting: blockSchema.nullable(),
  conflictType,
  conflictPaths: z.array(z.string()),
  conflictValues: z.array(
    z.object({
      source: z.unknown(),
      head: z.unknown(),
      common: z.unknown(),
    })
  ),
})

const blockChange = z.discriminatedUnion('status', [
  standardBlockChange,
  conflictBlockChange,
])

export const baseBlockChangeSchema = baseSchema.extend({
  title: z.string(),
  type: blockType,
  children: z
    .array(
      z.object({
        id: z.string(),
        status: z.enum([
          'modified',
          'unmodified',
          'created',
          'deleted',
          'reordered',
          'reordered-source',
          'conflict',
        ]),
        order: z.string(),
        conflictType: conflictType.optional(),
        conflictPaths: z.string().array().optional(),
      })
    )
    .nullable(),
  path: z.string(),
  nestedPrimitiveChanged: z.boolean().optional(),
})

export const blockChangeSchema = blockChange.and(baseBlockChangeSchema)

export type BlockChange = z.infer<typeof blockChangeSchema>
export type ConflictType = z.infer<typeof conflictType>
export type BlockChangeWithConflict = Extract<
  BlockChange,
  { status: 'conflict' }
>

export type BlockChangeByType<Type extends BlockType> = BlockChange & {
  type: Type
}

export type BlockChangeStatus = BlockChange['status']
export type BlockChangeChildStatus = NonNullable<
  BlockChange['children']
>[number]['status']

export * from './instance'
export * from './workflow'
export * from './tree-manager-helpers'
